import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ReadyState } from 'react-use-websocket';

import { gql, useQuery } from '@apollo/client';
import {
  Fade,
  Grid2,
  LinearProgress,
  Paper,
  SpeedDial,
  SpeedDialIcon,
  Typography
} from '@mui/material';

import { deviceIdSelector } from '../../app/main/mainSlice';
import { useAppSelector } from '../../app/store/hooks';
import { useWebSocketContext } from '../../core/UpWSConnect/UpWSConnect';
import { useUpAuth } from '../authentication/UpAuth';
import MiniplayerContent from './MiniPlayerContent';
import MiniplayerSkeleton from './MiniplayerSkeleton';
import { LidJarOff, LidJarOn, LidOffJarOn } from '../../utils/iconRefs/miniPlayerIcons';
import { MINI_PLAYER_URL } from "../../app/constants/constants";

export enum AppHardwareStatusEnum {
  DISCONNECTED = 'disconnected',
  IDLE = 'idle',
  OPERATION = 'operation',
  PAUSED = 'Paused',
  PAUSE_DISCONNECTED = 'pause_disconnected',
  OPERATION_IDLE = 'operation_idle',
  OPERATION_STOPPING = 'operation_stopping',
  PAUSE_RESUMING = 'pause_resuming',
  STARTING = 'starting',
  COOLDOWN = 'cooldown',
  FORCESTOP = 'Force Stop',
  PAUSE_RECONNECTING = 'pause_reconnecting',
  PAUSE_AUTORESUMING = 'pause_autoresuming',
}

const runningRecipeStates = [
  AppHardwareStatusEnum.OPERATION,
  AppHardwareStatusEnum.PAUSED,
  AppHardwareStatusEnum.COOLDOWN,
  AppHardwareStatusEnum.OPERATION_STOPPING,
  AppHardwareStatusEnum.PAUSE_RESUMING,
  AppHardwareStatusEnum.PAUSE_AUTORESUMING,
  AppHardwareStatusEnum.OPERATION_IDLE,
  AppHardwareStatusEnum.STARTING,
  AppHardwareStatusEnum.PAUSE_RECONNECTING,
  AppHardwareStatusEnum.PAUSE_DISCONNECTED
]

const deviceIdleStates = [
  AppHardwareStatusEnum.DISCONNECTED,
  AppHardwareStatusEnum.IDLE,
]

const GET_RECIPE_DATA_FOR_MINIPLAYER = gql(`
  query GET_RECIPE_DATA_FOR_MINIPLAYER($id: String = "") {
    Recipes_E3_by_pk(id: $id) {
      name
      Recipe_Variations(where: {default: {_eq: true}}) {
        image_url
      }
    }
  }
`);

const MiniplayerUI = () => {
  const [isOpen, setIsOpen] = useState(true);

  const {
    sendMessage,
    lastMessage,
    connectionStatus
  } = useWebSocketContext();

  const [recipeName, setRecipeName] = useState<string>("Recipe");
  const [recipeImageUrl, setRecipeImageUrl] = useState<string>("");
  const recipeId = lastMessage?.recipeId;
  const stateOfHardware: AppHardwareStatusEnum = lastMessage?.stateOfHardware ?? AppHardwareStatusEnum.DISCONNECTED;
  const recipeStatus = lastMessage?.status;
  const time = lastMessage?.time;
  const linearProgress = lastMessage?.linearProgress;
  const jarOn: boolean = lastMessage?.jarSelector;
  const lidOn: boolean = lastMessage?.lidSelector;
  const substeName = lastMessage?.currentSubstep?.name;
  const location = lastMessage?.currentSubstep?.parameters?.location;
  const substepType = lastMessage?.currentSubstep?.type;

  const { loading } = useQuery(GET_RECIPE_DATA_FOR_MINIPLAYER, {
    skip: !recipeId,
    variables: {
      id: recipeId
    },
    onCompleted: ({ Recipes_E3_by_pk }) => {
      setRecipeName(Recipes_E3_by_pk.name);
      setRecipeImageUrl(Recipes_E3_by_pk.Recipe_Variations[0].image_url);
    }
  });

  if (connectionStatus === ReadyState.CLOSED || connectionStatus === ReadyState.UNINSTANTIATED) {
    return null;
  }

  const progress = linearProgress || 0;
  const parsedProgress = runningRecipeStates.includes(stateOfHardware) ? parseFloat(progress.toFixed(2)) : 0;
  const shouldShowProgess = !deviceIdleStates.includes(stateOfHardware);

  if (loading) {
    return <MiniplayerSkeleton />;
  }

  if (!loading && !recipeId) {
    return null;
  }

  return (
    <Grid2>
      <Fade in={isOpen} >
        <Grid2>
          <MiniplayerContent
            jarOn={jarOn}
            lidOn={lidOn}
            location={location}
            parsedProgress={parsedProgress}
            recipeName={recipeName}
            recipeStatus={recipeStatus}
            shouldShowProgess={shouldShowProgess}
            stateOfHardware={stateOfHardware}
            substepName={substeName}
            substepType={substepType}
            time={time}
          />
        </Grid2>
      </Fade>
    </Grid2>
  );
};

const Miniplayer = () => {
  const location = useLocation();
  const allowedPaths = [
    MINI_PLAYER_URL.HOME,
    MINI_PLAYER_URL.EXPLORE,
    MINI_PLAYER_URL.MEAL_PLAN,
    MINI_PLAYER_URL.ACCOUNT
  ];

  if (!allowedPaths.includes(location.pathname)) {
    return null;
  }
  const deviceId = useAppSelector(deviceIdSelector);
  const { isAuthenticated, upAuthLoading } = useUpAuth()
  // if we don't want to show at any path you can decide that here
  if (upAuthLoading || !isAuthenticated || !deviceId) {
    return null
  }

  return (
    <MiniplayerUI />
  );
};

export default Miniplayer;